<template>
    <div>
        <b-modal
            :visible="visible"
            @update:visible="$emit('update:visible', $event)"
            @hide="onModalHide"
            title="배출계수 버전 관리"
            size="lg"
            centered
        >
            <template #modal-header>
                <h5 class="modal-title">배출계수 버전 관리</h5>
                <div class="header-actions">
                    <button type="button" class="close" @click="closeModal">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>

            <div v-if="factor" class="history-content">
                <!-- 배출계수 정보 -->
                <div class="factor-info-section">
                    <div class="factor-code">배출계수: {{ factor.factorCode }}</div>
                    <div class="factor-description">
                        {{ factor.description || `${factor.fuelType} (${factor.unit})` }}
                    </div>
                </div>

                <div class="section-divider">현재 유효 값</div>

                <!-- 현재 유효 값 (가스 상세) -->
                <div class="current-base-value">
                    <div class="value-box">
                        <div class="value-main">{{ getCurrentEmissionDisplay() }} {{ getCurrentDisplayUnit() }}</div>
                        <div v-if="currentValueItem" class="value-6gas">
                            CO2: {{ formatGasValue(getGasForItem(currentValueItem).CO2) }} | CH4:
                            {{ formatGasValue(getGasForItem(currentValueItem).CH4) }} | N2O:
                            {{ formatGasValue(getGasForItem(currentValueItem).N2O) }} | HFCs:
                            {{ formatGasValue(getGasForItem(currentValueItem).HFCs) }} | PFCs:
                            {{ formatGasValue(getGasForItem(currentValueItem).PFCs) }} | SF6:
                            {{ formatGasValue(getGasForItem(currentValueItem).SF6) }}
                        </div>
                        <div class="value-meta">
                            <span>출처: {{ currentValueItem?.source || factor.source || "-" }}</span>
                        </div>
                    </div>
                </div>

                <div class="section-divider">
                    배출계수 버전 목록
                    <button type="button" class="btn-add-value" @click="openAddForm" title="새 기간 값 추가">
                        <i class="fas fa-plus"></i> 버전 추가
                    </button>
                </div>

                <!-- 값 이력 테이블 (factor_values) - 가스 상세 -->
                <div class="history-table-wrapper">
                    <table class="history-table">
                        <thead>
                            <tr>
                                <th>유효 기간</th>
                                <th>합계</th>
                                <th>CO2</th>
                                <th>CH4</th>
                                <th>N2O</th>
                                <th>HFCs</th>
                                <th>PFCs</th>
                                <th>SF6</th>
                                <th>출처</th>
                                <th>GWP</th>
                                <th class="th-evidence">증빙</th>
                                <th class="th-actions">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loadingHistory">
                                <td colspan="12" class="text-center">로딩 중...</td>
                            </tr>
                            <tr v-else-if="historyList.length === 0">
                                <td colspan="12" class="text-center empty-cell">이력이 없습니다.</td>
                            </tr>
                            <tr
                                v-else
                                v-for="(item, index) in historyList"
                                :key="item.valueIdx || index"
                                :class="{ 'latest-row': item.endMonth == null }"
                            >
                                <td class="period-cell">
                                    {{ formatPeriod(item.startMonth, item.endMonth) }}
                                </td>
                                <td class="value-cell">{{ getEmissionDisplay(item) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).CO2) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).CH4) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).N2O) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).HFCs) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).PFCs) }}</td>
                                <td>{{ formatGasValue(getGasForItem(item).SF6) }}</td>
                                <td class="source-cell">{{ item.source || "-" }}</td>
                                <td class="gwp-cell">{{ item.gwpVersion || "-" }}</td>
                                <td class="evidence-cell">
                                    <button
                                        type="button"
                                        class="btn-evidence"
                                        :title="`증빙 ${getEvidenceCount(item)}건`"
                                        @click="openEvidenceModal(item)"
                                    >
                                        <i class="fas fa-paperclip"></i>
                                        {{ getEvidenceCount(item) }}건
                                    </button>
                                </td>
                                <td class="actions-cell">
                                    <button type="button" class="icon-btn-sm" title="수정" @click="openEditForm(item)">
                                        <i class="fas fa-edit"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="icon-btn-sm icon-btn-danger"
                                        title="삭제"
                                        @click="confirmDeleteValue(item)"
                                    >
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-else class="empty-message">
                <p>배출계수 정보를 불러올 수 없습니다.</p>
            </div>

            <template #modal-footer>
                <CarbonButton variant="secondary" @click="$emit('update:visible', false)">닫기</CarbonButton>
            </template>
        </b-modal>

        <!-- 버전 추가 모달 (260304: 가스) -->
        <b-modal v-model="showAddModal" title="버전 추가" size="md" centered @hidden="resetAddForm">
            <div class="add-value-form">
                <div class="form-group">
                    <label>계수 단위</label>
                    <select v-model="addForm.gasEmissionUnit" class="form-control">
                        <option v-for="opt in gasEmissionUnitOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-row-4gas-history">
                    <div class="form-group">
                        <label>CO2 <span class="required">*</span></label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="addForm.CO2Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="예: 2.2"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ addForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>CH4</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="addForm.CH4Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ addForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>N2O</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="addForm.N2OFactor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ addForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>SF6</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="addForm.SF6Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ addForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <FactorGasSpeciesBlock
                    basket="HFCs"
                    :rows="addForm.hfcSpeciesRows"
                    :gas-unit="addForm.gasEmissionUnit"
                    @update:rows="addForm.hfcSpeciesRows = $event"
                />
                <FactorGasSpeciesBlock
                    basket="PFCs"
                    :rows="addForm.pfcSpeciesRows"
                    :gas-unit="addForm.gasEmissionUnit"
                    @update:rows="addForm.pfcSpeciesRows = $event"
                />
                <div class="history-calorific-section">
                    <div class="history-calorific-title">열량계수 (선택)</div>
                    <p class="small text-muted mb-2">CALORIFIC_UNIT 단위</p>
                    <div class="calorific-cols-inline">
                        <div class="calorific-col-inline">
                            <label class="small calorific-label-inline">순발열량 (NCV)</label>
                            <div class="input-group input-group-sm calorific-inline">
                                <input
                                    v-model.number="addForm.ncvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값"
                                />
                                <select v-model="addForm.ncvUnit" class="form-control calorific-unit-inline">
                                    <option value="">단위</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="calorific-col-inline">
                            <label class="small calorific-label-inline">총발열량 (GCV)</label>
                            <div class="input-group input-group-sm calorific-inline">
                                <input
                                    v-model.number="addForm.gcvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값"
                                />
                                <select v-model="addForm.gcvUnit" class="form-control calorific-unit-inline">
                                    <option value="">단위</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>시작월 (YYYYMM) <span class="required">*</span></label>
                    <input
                        v-model="addForm.startMonth"
                        type="text"
                        class="form-control"
                        placeholder="202501"
                        maxlength="6"
                    />
                </div>
                <div class="form-group">
                    <label>출처/방법론</label>
                    <select v-model="addForm.source" class="form-control">
                        <option value="">선택</option>
                        <option v-for="opt in factorSourceOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>GWP</label>
                    <select v-model="addForm.gwpVersion" class="form-control">
                        <option value="">선택</option>
                        <option v-for="opt in gwpOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>비고</label>
                    <input v-model="addForm.remarks" type="text" class="form-control" placeholder="선택" />
                </div>
            </div>
            <template #modal-footer>
                <CarbonButton variant="secondary" :disabled="savingAdd" @click="showAddModal = false">취소</CarbonButton>
                <CarbonButton
                    variant="primary"
                    :disabled="!canSaveAdd"
                    :loading="savingAdd"
                    @click="submitAddValue"
                >
                    저장
                </CarbonButton>
            </template>
        </b-modal>

        <!-- 값 수정 모달 (260304: 가스) -->
        <b-modal v-model="showEditModal" title="배출계수 값 수정" size="md" centered @hidden="resetEditForm">
            <div v-if="editTarget" class="edit-value-form">
                <div class="form-group">
                    <label>계수 단위</label>
                    <select v-model="editForm.gasEmissionUnit" class="form-control">
                        <option v-for="opt in gasEmissionUnitOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-row-4gas-history">
                    <div class="form-group">
                        <label>CO2 <span class="required">*</span></label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="editForm.CO2Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="예: 2.2"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ editForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>CH4</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="editForm.CH4Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ editForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>N2O</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="editForm.N2OFactor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ editForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>SF6</label>
                        <div class="input-group input-group-sm">
                            <input
                                v-model.number="editForm.SF6Factor"
                                type="number"
                                step="0.0001"
                                min="0"
                                class="form-control"
                                placeholder="0"
                            />
                            <div class="input-group-append">
                                <span class="input-group-text history-gas-suffix">{{ editForm.gasEmissionUnit }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <FactorGasSpeciesBlock
                    basket="HFCs"
                    :rows="editForm.hfcSpeciesRows"
                    :gas-unit="editForm.gasEmissionUnit"
                    @update:rows="editForm.hfcSpeciesRows = $event"
                />
                <FactorGasSpeciesBlock
                    basket="PFCs"
                    :rows="editForm.pfcSpeciesRows"
                    :gas-unit="editForm.gasEmissionUnit"
                    @update:rows="editForm.pfcSpeciesRows = $event"
                />
                <div class="history-calorific-section">
                    <div class="history-calorific-title">열량계수 (선택)</div>
                    <div class="calorific-cols-inline">
                        <div class="calorific-col-inline">
                            <label class="small calorific-label-inline">순발열량 (NCV)</label>
                            <div class="input-group input-group-sm calorific-inline">
                                <input
                                    v-model.number="editForm.ncvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값"
                                />
                                <select v-model="editForm.ncvUnit" class="form-control calorific-unit-inline">
                                    <option value="">단위</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="calorific-col-inline">
                            <label class="small calorific-label-inline">총발열량 (GCV)</label>
                            <div class="input-group input-group-sm calorific-inline">
                                <input
                                    v-model.number="editForm.gcvValue"
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    class="form-control"
                                    placeholder="값"
                                />
                                <select v-model="editForm.gcvUnit" class="form-control calorific-unit-inline">
                                    <option value="">단위</option>
                                    <option v-for="opt in calorificUnitOptions" :key="opt.value" :value="opt.value">
                                        {{ opt.value }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>시작월 (YYYYMM)</label>
                    <input
                        v-model="editForm.startMonth"
                        type="text"
                        class="form-control"
                        placeholder="202501"
                        maxlength="6"
                    />
                </div>
                <div class="form-group">
                    <label>출처/방법론</label>
                    <select v-model="editForm.source" class="form-control">
                        <option value="">선택</option>
                        <option v-for="opt in factorSourceOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>GWP</label>
                    <select v-model="editForm.gwpVersion" class="form-control">
                        <option value="">선택</option>
                        <option v-for="opt in gwpOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                </div>
                <div class="form-group">
                    <label>비고</label>
                    <input v-model="editForm.remarks" type="text" class="form-control" placeholder="선택" />
                </div>
            </div>
            <template #modal-footer>
                <CarbonButton variant="secondary" :disabled="savingEdit" @click="showEditModal = false">취소</CarbonButton>
                <CarbonButton
                    variant="primary"
                    :disabled="!canSaveEdit"
                    :loading="savingEdit"
                    @click="submitEditValue"
                >
                    저장
                </CarbonButton>
            </template>
        </b-modal>

        <!-- 증빙파일 모달 (EvidenceModal 재사용) -->
        <EvidenceModal
            :visible="showEvidenceModal"
            evidence-category="FACTOR"
            :entity-idx="evidenceTarget?.valueIdx ?? null"
            :modal-title-override="evidenceModalTitle"
            @close="onEvidenceModalClose"
            @updated="onEvidenceUpdated"
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes";
import EvidenceModal from "@/submodules/carbon-plugin/components/activity/modals/EvidenceModal.vue";
import CarbonButton from "@/submodules/carbon-plugin/components/common/CarbonButton.vue";
import FactorGasSpeciesBlock from "./FactorGasSpeciesBlock.vue";
import {
    buildAllComponents,
    deriveCurrentValueAndUnit,
    getCalorificFromComponents,
    getGasFactorsFromComponents,
    splitSpeciesRowsFromComponents,
    basketPositiveTotals,
    validateUniqueGasComponentCodes,
} from "@/submodules/carbon-plugin/utils/factorComponentHelper";

function emptySpeciesRows() {
    return [{ componentCode: "", emissionFactor: null }];
}

export default {
    name: "FactorHistoryModal",
    components: { EvidenceModal, CarbonButton, FactorGasSpeciesBlock },

    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        factor: {
            type: Object,
            default: null,
        },
    },

    data() {
        const now = new Date();
        const startMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
        return {
            loadingHistory: false,
            historyList: [],
            showAddModal: false,
            addForm: {
                gasEmissionUnit: "kgGHG/TJ",
                CO2Factor: null,
                CH4Factor: 0,
                N2OFactor: 0,
                SF6Factor: 0,
                hfcSpeciesRows: emptySpeciesRows(),
                pfcSpeciesRows: emptySpeciesRows(),
                detailBasisHFCs: "",
                detailBasisPFCs: "",
                ncvValue: null,
                ncvUnit: "",
                gcvValue: null,
                gcvUnit: "",
                startMonth,
                source: "",
                gwpVersion: "",
                remarks: "",
            },
            savingAdd: false,
            showEditModal: false,
            editTarget: null,
            editForm: {
                gasEmissionUnit: "kgGHG/TJ",
                CO2Factor: null,
                CH4Factor: 0,
                N2OFactor: 0,
                SF6Factor: 0,
                hfcSpeciesRows: emptySpeciesRows(),
                pfcSpeciesRows: emptySpeciesRows(),
                detailBasisHFCs: "",
                detailBasisPFCs: "",
                ncvValue: null,
                ncvUnit: "",
                gcvValue: null,
                gcvUnit: "",
                startMonth: "",
                source: "",
                gwpVersion: "",
                remarks: "",
            },
            savingEdit: false,
            showEvidenceModal: false,
            evidenceTarget: null,
        };
    },

    computed: {
        currentValueItem() {
            return this.historyList.find((v) => v.endMonth == null) || null;
        },
        gwpOptions() {
            const list = CarbonCodes.gwpVersion?.getList?.();
            return list?.length > 0
                ? list
                : [
                      { value: "AR4", text: "AR4" },
                      { value: "AR5", text: "AR5" },
                      { value: "AR6", text: "AR6" },
                  ];
        },
        factorSourceOptions() {
            const list = CarbonCodes.factorSource?.getList?.() ?? [];
            return Array.isArray(list) ? list : [];
        },
        gasEmissionUnitOptions() {
            const activityUnits = CarbonCodes.activityUnit.getList();
            if (!Array.isArray(activityUnits) || activityUnits.length === 0) {
                return [
                    { value: "kgGHG/TJ", text: "kgGHG/TJ" },
                    { value: "kgGHG/kg", text: "kgGHG/kg" },
                ];
            }
            return activityUnits
                .map((item) => {
                    const denom = String(item?.value ?? "").trim();
                    if (!denom) return null;
                    const value = `kgGHG/${denom}`;
                    return { value, text: value };
                })
                .filter(Boolean);
        },
        calorificUnitOptions() {
            const list = CarbonCodes.calorificUnit?.getList?.() ?? [];
            if (Array.isArray(list) && list.length > 0) return list;
            return [
                { value: "MJ/Nm3", text: "MJ/Nm3" },
                { value: "TJ/1000Nm3", text: "TJ/1000Nm3" },
                { value: "TJ/ton", text: "TJ/ton" },
                { value: "MJ/kg", text: "MJ/kg" },
            ];
        },
        canSaveAdd() {
            const v = this.addForm.CO2Factor;
            const m = this.addForm.startMonth;
            return v != null && v >= 0 && /^\d{6}$/.test(m || "");
        },
        canSaveEdit() {
            const v = this.editForm.CO2Factor;
            return v != null && v >= 0;
        },
        evidenceModalTitle() {
            if (!this.evidenceTarget) return "";
            const period = this.formatPeriod(this.evidenceTarget.startMonth, this.evidenceTarget.endMonth);
            return `증빙파일 - ${period}`;
        },
    },

    watch: {
        visible(val) {
            if (val && this.factor?.factorIdx) {
                this.loadHistory();
            } else {
                this.historyList = [];
                this.showAddModal = false;
                this.showEvidenceModal = false;
            }
        },
        factor: {
            handler(val) {
                if (this.visible && val?.factorIdx) {
                    this.loadHistory();
                }
            },
            deep: true,
        },
    },

    methods: {
        /** X 버튼 클릭: Bootstrap Vue slot close()가 update:visible을 emit하지 않는 이슈 대응 */
        closeModal() {
            this.$emit("update:visible", false);
        },

        /** 모달 hide 시 (배경클릭, ESC, X 등) 부모와 동기화 보장 */
        onModalHide() {
            this.$emit("update:visible", false);
        },

        async loadHistory() {
            if (!this.factor?.factorIdx) return;
            this.loadingHistory = true;
            this.historyList = [];
            try {
                const res = await carbonApi.emissionFactor.getFactorValues(this.factor.factorIdx);
                const data = res.data?.data || res.data;
                const values = data?.values || data || [];
                this.historyList = Array.isArray(values) ? values : [];
            } catch (err) {
                console.error("[FactorHistoryModal] 값 이력 로드 실패:", err);
            } finally {
                this.loadingHistory = false;
            }
        },

        _currentYYYYMM() {
            const now = new Date();
            return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
        },

        openAddForm() {
            const gu =
                this.currentValueItem?.components?.find?.((c) => {
                    const code = String(c?.componentCode || "").trim();
                    return code && code !== "NCV" && code !== "GCV";
                })?.emissionUnit || "kgGHG/TJ";
            this.addForm = {
                gasEmissionUnit: gu,
                CO2Factor: null,
                CH4Factor: 0,
                N2OFactor: 0,
                SF6Factor: 0,
                hfcSpeciesRows: emptySpeciesRows(),
                pfcSpeciesRows: emptySpeciesRows(),
                detailBasisHFCs: "",
                detailBasisPFCs: "",
                ncvValue: null,
                ncvUnit: "",
                gcvValue: null,
                gcvUnit: "",
                startMonth: this._currentYYYYMM(),
                source: "",
                gwpVersion: "",
                remarks: "",
            };
            this.showAddModal = true;
        },

        resetAddForm() {
            this.addForm = {
                gasEmissionUnit: "kgGHG/TJ",
                CO2Factor: null,
                CH4Factor: 0,
                N2OFactor: 0,
                SF6Factor: 0,
                hfcSpeciesRows: emptySpeciesRows(),
                pfcSpeciesRows: emptySpeciesRows(),
                detailBasisHFCs: "",
                detailBasisPFCs: "",
                ncvValue: null,
                ncvUnit: "",
                gcvValue: null,
                gcvUnit: "",
                startMonth: this._currentYYYYMM(),
                source: "",
                gwpVersion: "",
                remarks: "",
            };
        },

        appendDetailBasisFromForm(payload, form, hfcs, pfcs) {
            const db = {};
            if (hfcs > 0) {
                const s = String(form.detailBasisHFCs || "").trim();
                if (s) db.HFCsFactor = { summary: s };
            }
            if (pfcs > 0) {
                const s = String(form.detailBasisPFCs || "").trim();
                if (s) db.PFCsFactor = { summary: s };
            }
            if (Object.keys(db).length) payload.detailBasis = db;
        },

        async submitAddValue() {
            if (!this.canSaveAdd || !this.factor?.factorIdx) return;
            const { hfcs, pfcs } = basketPositiveTotals(this.addForm.hfcSpeciesRows, this.addForm.pfcSpeciesRows);
            const components = buildAllComponents(this.addForm, {
                gasEmissionUnit: this.addForm.gasEmissionUnit,
                hfcSpeciesRows: this.addForm.hfcSpeciesRows,
                pfcSpeciesRows: this.addForm.pfcSpeciesRows,
                ncvValue: this.addForm.ncvValue,
                ncvUnit: this.addForm.ncvUnit,
                gcvValue: this.addForm.gcvValue,
                gcvUnit: this.addForm.gcvUnit,
            });
            const uniq = validateUniqueGasComponentCodes(components);
            if (!uniq.ok) {
                this.$bvToast.toast(`가스 componentCode가 중복입니다: ${uniq.duplicate}`, {
                    variant: "danger",
                    solid: true,
                });
                return;
            }
            this.savingAdd = true;
            try {
                const payload = {
                    startMonth: this.addForm.startMonth,
                    source: this.addForm.source || null,
                    remarks: this.addForm.remarks || null,
                    components,
                };
                this.appendDetailBasisFromForm(payload, this.addForm, hfcs, pfcs);
                await carbonApi.emissionFactor.addFactorValue(this.factor.factorIdx, payload);
                this.$bvToast.toast("배출계수 값이 추가되었습니다.", { variant: "success", solid: true });
                this.showAddModal = false;
                await this.loadHistory();
                this.$emit("value-added");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "값 추가에 실패했습니다.";
                this.$bvToast.toast(msg, { variant: "danger", solid: true });
            } finally {
                this.savingAdd = false;
            }
        },

        openEditForm(item) {
            this.editTarget = item;
            const cal = getCalorificFromComponents(item);
            const comps = item.components;
            const gasUnit =
                Array.isArray(comps) && comps.length > 0
                    ? comps.find?.((c) => {
                          const code = String(c?.componentCode || "").trim();
                          return code && code !== "NCV" && code !== "GCV";
                      })?.emissionUnit || "kgGHG/TJ"
                    : this.factor?.unit || "kgGHG/TJ";

            let edit;
            if (Array.isArray(comps) && comps.length > 0) {
                const split = splitSpeciesRowsFromComponents(comps);
                edit = {
                    gasEmissionUnit: gasUnit,
                    CO2Factor: split.CO2Factor != null ? split.CO2Factor : null,
                    CH4Factor: split.CH4Factor,
                    N2OFactor: split.N2OFactor,
                    SF6Factor: split.SF6Factor,
                    hfcSpeciesRows: split.hfcSpeciesRows,
                    pfcSpeciesRows: split.pfcSpeciesRows,
                };
            } else {
                const gas = getGasFactorsFromComponents(item);
                edit = {
                    gasEmissionUnit: gasUnit,
                    CO2Factor: item.CO2Factor != null ? Number(item.CO2Factor) : null,
                    CH4Factor: item.CH4Factor != null ? Number(item.CH4Factor) : 0,
                    N2OFactor: item.N2OFactor != null ? Number(item.N2OFactor) : 0,
                    SF6Factor: item.SF6Factor != null ? Number(item.SF6Factor) : 0,
                    hfcSpeciesRows:
                        gas.HFCs > 0 ? [{ componentCode: "HFCs", emissionFactor: gas.HFCs }] : emptySpeciesRows(),
                    pfcSpeciesRows:
                        gas.PFCs > 0 ? [{ componentCode: "PFCs", emissionFactor: gas.PFCs }] : emptySpeciesRows(),
                };
            }
            this.editForm = {
                ...edit,
                detailBasisHFCs: item.detailBasis?.HFCsFactor?.summary || "",
                detailBasisPFCs: item.detailBasis?.PFCsFactor?.summary || "",
                ncvValue: cal.ncv,
                ncvUnit: cal.ncvUnit || "",
                gcvValue: cal.gcv,
                gcvUnit: cal.gcvUnit || "",
                startMonth: item.startMonth || "",
                source: item.source || "",
                gwpVersion: item.gwpVersion || "",
                remarks: item.remarks || "",
            };
            this.showEditModal = true;
        },

        resetEditForm() {
            this.editTarget = null;
            this.editForm = {
                gasEmissionUnit: "kgGHG/TJ",
                CO2Factor: null,
                CH4Factor: 0,
                N2OFactor: 0,
                SF6Factor: 0,
                hfcSpeciesRows: emptySpeciesRows(),
                pfcSpeciesRows: emptySpeciesRows(),
                detailBasisHFCs: "",
                detailBasisPFCs: "",
                ncvValue: null,
                ncvUnit: "",
                gcvValue: null,
                gcvUnit: "",
                startMonth: "",
                source: "",
                gwpVersion: "",
                remarks: "",
            };
        },

        async submitEditValue() {
            if (!this.canSaveEdit || !this.editTarget || !this.factor?.factorIdx) return;
            const { hfcs, pfcs } = basketPositiveTotals(this.editForm.hfcSpeciesRows, this.editForm.pfcSpeciesRows);
            const components = buildAllComponents(this.editForm, {
                gasEmissionUnit: this.editForm.gasEmissionUnit,
                hfcSpeciesRows: this.editForm.hfcSpeciesRows,
                pfcSpeciesRows: this.editForm.pfcSpeciesRows,
                ncvValue: this.editForm.ncvValue,
                ncvUnit: this.editForm.ncvUnit,
                gcvValue: this.editForm.gcvValue,
                gcvUnit: this.editForm.gcvUnit,
            });
            const uniq = validateUniqueGasComponentCodes(components);
            if (!uniq.ok) {
                this.$bvToast.toast(`가스 componentCode가 중복입니다: ${uniq.duplicate}`, {
                    variant: "danger",
                    solid: true,
                });
                return;
            }
            this.savingEdit = true;
            try {
                const payload = {
                    startMonth: this.editForm.startMonth || undefined,
                    source: this.editForm.source || null,
                    remarks: this.editForm.remarks || null,
                    components,
                };
                this.appendDetailBasisFromForm(payload, this.editForm, hfcs, pfcs);
                await carbonApi.emissionFactor.updateFactorValue(
                    this.factor.factorIdx,
                    this.editTarget.valueIdx,
                    payload,
                );
                this.$bvToast.toast("배출계수 값이 수정되었습니다.", { variant: "success", solid: true });
                this.showEditModal = false;
                await this.loadHistory();
                this.$emit("value-updated");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "값 수정에 실패했습니다.";
                this.$bvToast.toast(msg, { variant: "danger", solid: true });
            } finally {
                this.savingEdit = false;
            }
        },

        confirmDeleteValue(item) {
            const period = this.formatPeriod(item.startMonth, item.endMonth);
            this.$bvModal
                .msgBoxConfirm(`기간 "${period}"의 값을 삭제하시겠습니까?`, {
                    title: "값 삭제 확인",
                    okVariant: "danger",
                    okTitle: "삭제",
                    cancelTitle: "취소",
                    centered: true,
                })
                .then((confirmed) => {
                    if (confirmed) this.deleteValue(item);
                })
                .catch(() => {});
        },

        async deleteValue(item) {
            if (!this.factor?.factorIdx || !item.valueIdx) return;
            try {
                await carbonApi.emissionFactor.deleteFactorValue(this.factor.factorIdx, item.valueIdx);
                this.$bvToast.toast("배출계수 값이 삭제되었습니다.", { variant: "success", solid: true });
                await this.loadHistory();
                this.$emit("value-deleted");
            } catch (err) {
                const msg = err.response?.data?.message || err.message || "값 삭제에 실패했습니다.";
                this.$bvToast.toast(msg, { variant: "danger", solid: true });
            }
        },

        formatPeriod(startMonth, endMonth) {
            if (!startMonth) return "-";
            const start = startMonth ? `${String(startMonth).slice(0, 4)}-${String(startMonth).slice(4, 6)}` : "";
            const end = endMonth ? `${String(endMonth).slice(0, 4)}-${String(endMonth).slice(4, 6)}` : "현재";
            return `${start} ~ ${end}`;
        },

        getEvidenceCount(item) {
            const files = item?.evidenceFiles;
            return Array.isArray(files) ? files.length : 0;
        },

        /** 260304: components 기반 합계 또는 legacy emissionFactor/factorValue 표시 */
        getEmissionDisplay(item) {
            const { currentValue } = deriveCurrentValueAndUnit(item);
            if (currentValue != null) return currentValue;
            return item?.emissionFactor ?? item?.factorValue ?? "-";
        },

        /** 260304: factor 배출계수 표시 - emissionFactor 또는 currentValue/factor */
        getFactorEmissionDisplay(factor) {
            return factor?.emissionFactor ?? factor?.currentValue ?? factor?.factor ?? "-";
        },

        getCurrentEmissionDisplay() {
            if (this.currentValueItem) {
                const { currentValue } = deriveCurrentValueAndUnit(this.currentValueItem);
                if (currentValue != null) return currentValue;
                return this.currentValueItem.emissionFactor ?? this.currentValueItem.factorValue ?? "-";
            }
            return this.getFactorEmissionDisplay(this.factor);
        },

        getCurrentDisplayUnit() {
            if (this.currentValueItem) {
                const { displayUnit } = deriveCurrentValueAndUnit(this.currentValueItem);
                if (displayUnit && displayUnit !== "-") return displayUnit;
            }
            return this.factor?.unit ?? "-";
        },

        formatGasValue(val) {
            if (val == null || val === undefined) return "-";
            const n = Number(val);
            return n === 0 ? "0" : n;
        },

        /** components 또는 legacy 플랫 필드에서 가스별 값 맵 반환 (테이블 표시용) */
        getGasForItem(item) {
            return getGasFactorsFromComponents(item);
        },

        async openEvidenceModal(item) {
            if (!item?.valueIdx) return;
            this.evidenceTarget = item;
            this.showEvidenceModal = true;
        },

        onEvidenceModalClose() {
            this.showEvidenceModal = false;
            this.evidenceTarget = null;
        },

        async onEvidenceUpdated() {
            await this.loadHistory();
            this.$emit("value-updated");
        },
    },
};
</script>

<style scoped>
.history-content {
    padding: 10px 0;
}

/* 헤더 액션 */
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-actions .icon-btn {
    padding: 6px 12px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    color: #6c757d;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s ease;
}

.header-actions .icon-btn:hover {
    color: #2d8659;
    border-color: #52b788;
}

/* 배출계수 정보 */
.factor-info-section {
    background: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 16px;
}

.factor-code {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: #2d8659;
}

.factor-description {
    margin: 0;
    font-size: 13px;
    color: #6c757d;
}

/* Section Divider */
.section-divider {
    font-size: 13px;
    font-weight: 600;
    color: #495057;
    margin: 16px 0 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.btn-add-value {
    padding: 4px 10px;
    font-size: 12px;
    border: 1px solid #52b788;
    border-radius: 4px;
    background: #f0f9f4;
    color: #2d8659;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-add-value:hover {
    background: #2d8659;
    color: white;
    border-color: #2d8659;
}

.form-row-4gas-history {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 10px;
}

.add-value-form .form-group,
.edit-value-form .form-group {
    margin-bottom: 12px;
}

.form-row-4gas-history .form-group {
    margin-bottom: 0;
}

.history-gas-suffix {
    font-size: 11px;
    color: #475569;
    max-width: 9rem;
    white-space: normal;
    word-break: break-word;
    line-height: 1.25;
    text-align: left;
}

.history-calorific-section {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e8eaed;
}

.history-calorific-title {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 4px;
}

.add-value-form .form-group:last-child {
    margin-bottom: 0;
}

.add-value-form .form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
}

.add-value-form .required {
    color: #dc3545;
}

.th-evidence {
    width: 70px;
    text-align: center;
}

.th-actions {
    width: 80px;
    text-align: center;
}

.evidence-cell {
    text-align: center;
}

.btn-evidence {
    padding: 2px 8px;
    font-size: 11px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    color: #495057;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-evidence:hover {
    background: #e9ecef;
    border-color: #52b788;
    color: #2d8659;
}

.actions-cell {
    white-space: nowrap;
    text-align: center;
}

.icon-btn-sm {
    padding: 4px 8px;
    border: none;
    background: transparent;
    color: #6c757d;
    cursor: pointer;
    border-radius: 4px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.icon-btn-sm:hover {
    background: #e9ecef;
    color: #495057;
}

.icon-btn-sm.icon-btn-danger:hover {
    background: #f8d7da;
    color: #721c24;
}

.edit-value-form .form-group {
    margin-bottom: 12px;
}

.edit-value-form .form-group:last-child {
    margin-bottom: 0;
}

.edit-value-form .form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
}

.edit-value-form .required {
    color: #dc3545;
}

/* 현재 기본 값 */
.current-base-value {
    margin-bottom: 16px;
}

.value-box {
    background: #f8f9fa;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.value-main {
    font-size: 18px;
    font-weight: 700;
    color: #2d8659;
    margin-bottom: 6px;
}

.value-6gas {
    font-size: 12px;
    color: #495057;
    margin-bottom: 6px;
    line-height: 1.5;
}

.value-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: #6c757d;
}

/* 이력 테이블 */
.history-table-wrapper {
    margin-bottom: 16px;
    overflow-x: auto;
}

.history-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    font-size: 12px;
}

.history-table thead {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}

.history-table th {
    padding: 8px 10px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #495057;
}

.history-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 12px;
}

.history-table tbody tr:last-child td {
    border-bottom: none;
}

.history-table tbody tr:hover {
    background: #f8f9fa;
}

.history-table .latest-row {
    background: #f0f9f4;
}

.history-table .period-cell {
    font-weight: 600;
    color: #2d8659;
}

.history-table .source-cell,
.history-table .gwp-cell {
    font-size: 12px;
}

.history-table .value-cell {
    font-weight: 600;
    color: #2d8659;
}

.history-table .type-cell {
    text-align: center;
}

.type-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
}

.type-badge.version {
    background: #fff3cd;
    color: #856404;
}

.type-badge.base {
    background: #e9ecef;
    color: #495057;
}

.history-table .remarks-cell {
    color: #6c757d;
    font-size: 12px;
}

.history-table .date-cell {
    font-size: 12px;
    color: #6c757d;
}

.latest-tag {
    margin-left: 6px;
    color: #2d8659;
    font-weight: 600;
    font-size: 11px;
}

/* 범례 */
.legend-box {
    padding: 10px;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 12px;
    color: #495057;
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
}

.legend-box strong {
    margin-right: 6px;
}

.legend-item {
    display: inline-flex;
    align-items: center;
}

/* 사용 통계 */
.usage-stats {
    background: #ffffff;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.usage-stats h5 {
    font-size: 13px;
    margin-bottom: 12px;
    color: #495057;
    font-weight: 600;
}

.stat-bar-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.stat-bar-row:last-child {
    margin-bottom: 0;
}

.stat-version {
    min-width: 90px;
    font-weight: 600;
    font-size: 12px;
    color: #495057;
}

.stat-bar-container {
    flex: 1;
    height: 24px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.stat-bar {
    height: 100%;
    background: linear-gradient(90deg, #52b788 0%, #2d8659 100%);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;
    transition: width 0.5s ease;
}

.stat-count {
    color: white;
    font-weight: 600;
    font-size: 11px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.usage-summary {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
    font-size: 12px;
    color: #6c757d;
}

.usage-summary strong {
    color: #2d8659;
    font-weight: 700;
}

/* Empty Message */
.empty-message {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
}

.empty-message p {
    margin-top: 12px;
    font-size: 13px;
}

.empty-cell {
    text-align: center;
    color: #6c757d;
    padding: 30px 20px !important;
}

/* 열량계수: 라벨 위, 그 아래 값+단위 가로 배치. 좌 순발열량 | 우 총발열량 */
.calorific-cols-inline {
    display: flex;
    flex-wrap: nowrap;
    gap: 16px;
}
.calorific-col-inline {
    flex: 1;
    min-width: 0;
}
.calorific-label-inline {
    display: block;
    margin-bottom: 4px;
}
.calorific-inline {
    display: flex;
    flex-wrap: nowrap;
}
.calorific-inline .form-control:first-child {
    flex: 1;
    min-width: 60px;
}
.calorific-unit-inline {
    flex-shrink: 0;
    width: 100px;
}
</style>
